import React, { useState } from "react";
import {
  Button,
  Checkbox,
  Popover,
  InputAdornment,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import IndeterminateCheckBoxOutlinedIcon from "@mui/icons-material/IndeterminateCheckBoxOutlined";
import "./CompanyFilterDropdown.css";

const companyIdOf = (company) =>
  company?._id != null ? String(company._id) : "";

export const CompanyFilterDropdown = ({
  companies = [],
  appliedIds = [],
  onApply,
  disabled,
  className = "",
  variant = "modal",
  itemLabel = "Company",
  itemLabelPlural = "Companies",
  searchPlaceholder = "Search companies...",
  allLabel = "All companies",
  emptyLabel = "No companies found",
  ariaLabel = "Filter by company",
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [query, setQuery] = useState("");
  const [draftIds, setDraftIds] = useState(appliedIds);

  const open = Boolean(anchorEl);
  const isToolbar = variant === "toolbar";
  const allIds = companies.map(companyIdOf).filter(Boolean);
  const appliedCount = appliedIds.length;
  const allApplied =
    allIds.length > 0 &&
    allIds.every((id) => appliedIds.map(String).includes(id));
  const allDraftSelected =
    allIds.length > 0 && allIds.every((id) => draftIds.includes(id));
  const someDraftSelected = draftIds.length > 0 && !allDraftSelected;
  const hasActiveFilter = appliedCount > 0 && !allApplied;

  const toolbarLabel = (() => {
    if (!hasActiveFilter) return itemLabel;
    if (appliedCount === 1) {
      const match = companies.find(
        (company) => companyIdOf(company) === String(appliedIds[0])
      );
      return match?.name || itemLabel;
    }
    return `${appliedCount} ${itemLabelPlural}`;
  })();

  const openMenu = (event) => {
    if (disabled) return;
    setDraftIds(appliedIds.map(String));
    setQuery("");
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = () => {
    setAnchorEl(null);
    setQuery("");
    setDraftIds(appliedIds.map(String));
  };

  const toggleCompany = (id) => {
    const key = String(id);
    setDraftIds((prev) =>
      prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]
    );
  };

  const toggleAllCompanies = () => {
    setDraftIds(allDraftSelected ? [] : allIds);
  };

  const handleApply = () => {
    onApply?.(draftIds.map(String));
    setAnchorEl(null);
    setQuery("");
  };

  const handleClearAll = () => {
    setDraftIds([]);
  };

  const needle = query.trim().toLowerCase();
  const matchesQuery = (company) =>
    !needle || String(company.name || "").toLowerCase().includes(needle);

  const selectedCompanies = companies.filter(
    (c) => draftIds.includes(companyIdOf(c)) && matchesQuery(c)
  );
  const remainingCompanies = companies.filter((c) => {
    if (draftIds.includes(companyIdOf(c))) return false;
    return matchesQuery(c);
  });

  return (
    <>
      <button
        type="button"
        className={`company-filter__btn${
          isToolbar
            ? " company-filter__btn--toolbar"
            : " company-filter__btn--labeled"
        }${hasActiveFilter ? " company-filter__btn--active" : ""}${
          open ? " company-filter__btn--open" : ""
        }${className ? ` ${className}` : ""}`}
        aria-label={ariaLabel}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={openMenu}
        disabled={disabled}
      >
        {isToolbar ? (
          <span className="company-filter__toolbar-label">{toolbarLabel}</span>
        ) : (
          <>
            <span className="company-filter__label">{itemLabel}</span>
            {hasActiveFilter && (
              <span className="company-filter__count">
                {appliedCount}{" "}
                {appliedCount === 1 ? itemLabel : itemLabelPlural} Selected
              </span>
            )}
          </>
        )}
        {isToolbar ? (
          <ArrowForwardIosIcon className="company-filter__toolbar-chevron" />
        ) : (
          <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
        )}
      </button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ zIndex: 1400 }}
        PaperProps={{ className: "company-filter__menu" }}
      >
        <div className="company-filter__search">
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder={searchPlaceholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: "#98a2b3" }} />
                </InputAdornment>
              ),
            }}
          />
        </div>
        <div className="company-filter__list">
          {allIds.length > 0 && (
            <label className="company-filter__item company-filter__item--all">
              <Checkbox
                size="small"
                checked={allDraftSelected}
                indeterminate={someDraftSelected}
                indeterminateIcon={<IndeterminateCheckBoxOutlinedIcon />}
                onChange={toggleAllCompanies}
                sx={{
                  color: "var(--grey400)",
                  "&.Mui-checked": { color: "var(--pblue)" },
                  "&.MuiCheckbox-indeterminate": { color: "var(--pblue)" },
                }}
              />
              <span>{allLabel}</span>
            </label>
          )}
          {selectedCompanies.length > 0 && (
            <>
              <p className="company-filter__heading">Selected</p>
              {selectedCompanies.map((company) => (
                <label
                  key={`selected-${company._id}`}
                  className="company-filter__item"
                >
                  <Checkbox
                    size="small"
                    checked
                    onChange={() => toggleCompany(companyIdOf(company))}
                    sx={{
                      color: "var(--grey400)",
                      "&.Mui-checked": { color: "var(--pblue)" },
                    }}
                  />
                  <span className="company-filter__item-name">
                    {company.name}
                  </span>
                </label>
              ))}
            </>
          )}
          {remainingCompanies.length > 0 && (
            <>
              {selectedCompanies.length > 0 && (
                <p className="company-filter__heading">{itemLabelPlural}</p>
              )}
              {remainingCompanies.map((company) => (
                <label key={company._id} className="company-filter__item">
                  <Checkbox
                    size="small"
                    checked={false}
                    onChange={() => toggleCompany(companyIdOf(company))}
                    sx={{ color: "var(--grey400)" }}
                  />
                  <span className="company-filter__item-name">
                    {company.name}
                  </span>
                </label>
              ))}
            </>
          )}
          {selectedCompanies.length === 0 &&
            remainingCompanies.length === 0 && (
              <p className="company-filter__empty">{emptyLabel}</p>
            )}
        </div>
        <div className="company-filter__footer">
          <Button
            type="button"
            className="company-filter__clear"
            onClick={handleClearAll}
            disabled={!draftIds.length}
          >
            Clear All
          </Button>
          <Button
            type="button"
            variant="contained"
            disableElevation
            className="company-filter__apply"
            onClick={handleApply}
          >
            Apply
          </Button>
        </div>
      </Popover>
    </>
  );
};
